import { format, isValid } from "date-fns";
import { toZonedTime } from "date-fns-tz";

interface useDateTimeProps {
  dateFormat?: string;
  dateTimeFormat?: string;
  timeFormat?: string;
}
export function useDateTime({
  dateFormat = "yyyy-MM-dd",
  dateTimeFormat = "yyyy-MM-dd p",
  timeFormat = "p",
}: useDateTimeProps) {
  const format12HourWithUTC = (date: string) => {
    if (!date || date === "") return "";

    const withUTC = `${date} UTC`;
    const newDateFormat = new Date(withUTC);

    const newDate = format(newDateFormat, "yyyy/MM/dd h:mm a");
    return newDate;
  };

  const formatDate = (
    date: Date | string | null | undefined,
    defaultDisplay: string = ""
  ) => {
    let displayDate = defaultDisplay;
    if (typeof date === "string") {
      const dateIsValid = isValid(new Date(date));
      displayDate =
        date && dateIsValid
          ? format(new Date(date), dateFormat)
          : defaultDisplay;
    } else {
      displayDate = date ? format(date, dateFormat) : defaultDisplay;
    }

    return displayDate;
  };

  const formatDateTime = (
    date: Date | string | null | undefined,
    defaultDisplay: string = ""
  ) => {
    let displayDate = defaultDisplay;
    if (typeof date === "string") {
      const dateIsValid = isValid(new Date(date));
      displayDate =
        date && dateIsValid
          ? format(new Date(date), dateTimeFormat)
          : defaultDisplay;
    } else {
      displayDate = date ? format(date, dateTimeFormat) : defaultDisplay;
    }

    return displayDate;
  };

  const getTime = (time: string) => {
    const dateOnly = new Date().toISOString().replace(/T.*$/, "");
    return `${dateOnly}T${time}:00`;
  };

  const formatTime = (
    date: Date | string | null | undefined,
    defaultDisplay: string = ""
  ) => {
    const timeFormat = "h:mm a";
    let displayDate = defaultDisplay;
    if (typeof date === "string") {
      const dateIsValid = isValid(new Date(date));
      displayDate =
        date && dateIsValid
          ? format(new Date(date), timeFormat)
          : defaultDisplay;
    } else {
      displayDate = date ? format(date, timeFormat) : defaultDisplay;
    }

    return displayDate;
  };

  const formatDateTimeForInput = (
    date: Date | string | null | undefined,
    defaultDisplay: string = ""
  ) => {
    const inputFormat = "yyyy-MM-dd hh:mm";
    let displayDate = defaultDisplay;
    if (typeof date === "string") {
      const dateIsValid = isValid(new Date(date));
      displayDate =
        date && dateIsValid
          ? format(new Date(date), inputFormat)
          : defaultDisplay;
    } else {
      displayDate = date ? format(date, inputFormat) : defaultDisplay;
    }

    return displayDate;
  };

  const formatDateLocal = (date: string | Date, timezone?: string): string => {
    const zonedDate = getDateTimeLocal(date, timezone);
    return format(zonedDate, dateFormat);
  };

  const formatDateTimeLocal = (
    date: string | Date,
    timezone?: string
  ): string => {
    const zonedDate = getDateTimeLocal(date, timezone);
    return format(zonedDate, dateTimeFormat);
  };

  const formatTimeLocal = (date: string | Date, timezone?: string): string => {
    const zonedDate = getDateTimeLocal(date, timezone);
    return format(zonedDate, timeFormat);
  };

  const getDateTimeLocal = (date: string | Date, timezone?: string): Date => {
    const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const selectedTimezone = timezone ? timezone : defaultTimezone;
    const converted = typeof date === "string" ? convertToISO8601(date) : date;
    const zonedDate = toZonedTime(converted, selectedTimezone);
    return zonedDate;
  };
  const convertToISO8601 = (dateString: string): string => {
    // Check if the string already follows ISO 8601 format (contains 'T' and ends with 'Z')
    if (dateString.includes("T") && dateString.endsWith("Z")) {
      return dateString;
    }

    // Check if the format is 'YYYY-MM-DD HH:mm:ss[.SSSSSS]'
    const datetimeWithMicrosecondsRegex =
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/;
    if (datetimeWithMicrosecondsRegex.test(dateString)) {
      // Replace the space with 'T' to make it ISO 8601 compatible
      const cleanedDateString =
        dateString.split(" ")[0] + "T" + dateString.split(" ")[1];
      return cleanedDateString.replace(/\.\d+$/, "") + "Z";
    }

    // Check if the format is 'DD-MM-YYYY HH:mm:ss'
    const [day, month, year, time] = dateString.split(/[- :]/);
    if (!isNaN(Number(day)) && !isNaN(Number(month)) && !isNaN(Number(year))) {
      const utcDate = new Date(
        Date.UTC(
          Number(year),
          Number(month) - 1,
          Number(day),
          ...time.split(":").map(Number)
        )
      );

      // Ensure the date is valid
      if (isNaN(utcDate.getTime())) {
        throw new Error("Invalid date string format");
      }

      // Convert to ISO 8601 format with a 'Z' to indicate UTC
      return utcDate.toISOString().replace(/\.\d{3}Z$/, "Z");
    }

    // If no valid format is detected, throw an error
    throw new Error("Invalid date string format");
  };

  return {
    dateFormat,
    dateTimeFormat,
    format12HourWithUTC,
    formatDate,
    formatDateTime,
    formatDateTimeForInput,
    formatTime,
    getTime,
    formatDateTimeLocal,
    formatTimeLocal,
    formatDateLocal,
    getDateTimeLocal,
    convertToISO8601,
  };
}
